/**
 * Page-level slide-out shop drawer. Mounted at the end of <body> (outside the
 * header) so its fixed panel anchors to the viewport, not the header box. Opened
 * by any `[data-shop-drawer-open]` element anywhere on the page (the desktop
 * "Shop" nav item and the mobile hamburger), closed by the backdrop, the close
 * button (`[data-shop-drawer-close]`), or Escape. Focus is trapped while open.
 */
class ShopDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.shop-drawer__panel');
    this.inert = !this.isOpen;
    this._background = [];
    this._syncOpeners(false);

    this._onDocClick = (event) => {
      const opener = event.target.closest('[data-shop-drawer-open]');
      if (opener) {
        event.preventDefault();
        this._opener = opener;
        this.open();
        return;
      }
      const closer = event.target.closest('[data-shop-drawer-close]');
      if (closer && this.contains(closer)) {
        event.preventDefault();
        this.close();
      }
    };

    this._onKeydown = (event) => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'Tab') {
        this._trapFocus(event);
      }
    };

    // Leaving the page with the drawer open (tapping a tile, say) put the page
    // into the browser's back-forward cache mid-drawer, and pressing Back
    // restored it half-drawn: the panel's frame over a repainted page. Close it,
    // with no animation, as the page is put away, and again if a page comes
    // back with it open, so Back always returns to a clean page.
    this._onPageHide = () => this._closeInstantly();
    this._onPageShow = (event) => { if (event.persisted) this._closeInstantly(); };

    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKeydown);
    window.addEventListener('pagehide', this._onPageHide);
    window.addEventListener('pageshow', this._onPageShow);
  }

  _closeInstantly() {
    if (!this.isOpen) return;
    this.classList.add('shop-drawer--instant');
    this._opener = null; // no focus jump on a page the user is leaving
    this.close();
    void this.offsetWidth; // commit the closed state before transitions return
    requestAnimationFrame(() => this.classList.remove('shop-drawer--instant'));
  }

  disconnectedCallback() {
    clearTimeout(this._focusTimer);
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('pagehide', this._onPageHide);
    window.removeEventListener('pageshow', this._onPageShow);
    document.documentElement.classList.remove('shop-drawer-open');
    this._restoreBackground();
    this._syncOpeners(false);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;
    this.inert = false;
    this.setAttribute('open', '');
    this._syncOpeners(true);
    this._background = [...document.querySelectorAll('.page-wrapper')]
      .filter(element => !element.contains(this)).map(element => [element, element.inert]);
    this._background.forEach(([element]) => { element.inert = true; });
    // Scroll-lock the page behind the drawer.
    document.documentElement.classList.add('shop-drawer-open');
    // Move focus into the panel once the slide-in starts.
    requestAnimationFrame(() => {
      if (!this.isOpen) return;
      const first = this._focusables()[0];
      if (first) first.focus();
      // A transitioning visibility value can reject focus on the opening frame.
      this._focusTimer = setTimeout(() => {
        if (this.isOpen && !this.contains(document.activeElement)) this._focusables()[0]?.focus();
      }, 350);
    });
  }

  close() {
    if (!this.isOpen) return;
    clearTimeout(this._focusTimer);
    this.removeAttribute('open');
    this._restoreBackground();
    this._syncOpeners(false);
    this.inert = true;
    document.documentElement.classList.remove('shop-drawer-open');
    // Return focus to whatever opened the drawer.
    if (this._opener && typeof this._opener.focus === 'function') this._opener.focus();
  }

  _syncOpeners(open) {
    document.querySelectorAll('[data-shop-drawer-open]').forEach(opener => {
      opener.setAttribute('aria-expanded', String(open));
      opener.setAttribute('aria-controls', this.id);
    });
  }

  _restoreBackground() {
    this._background?.forEach(([element, inert]) => { element.inert = inert; });
    this._background = [];
  }

  _focusables() {
    if (!this.panel) return [];
    return [...this.panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')].filter(
      (el) => el.offsetParent !== null
    );
  }

  _trapFocus(event) {
    const focusables = this._focusables();
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !this.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('shop-drawer')) {
  customElements.define('shop-drawer', ShopDrawer);
}
